import { useCanvasStore } from '../../store/canvas-store';
import { shortId } from '../dropDetect';
import { snapTime } from './timeMap';
import { sampleValueAt } from './sampleValueAt';
import { nodeLabel } from './pickOnCanvas';
import { liveCfElement } from '../layerSelect';
import type { MotionTimeline } from '../../store/types';

export interface RecordDragCommit {
  frameId: string;
  cfNodeId: string;
  /** Playhead time (ms) the drag keys at. */
  t: number;
  /** Pointer delta in screen px. */
  dx: number;
  dy: number;
  zoom: number;
}

/** Read the (possibly just-dispatched) frame's timeline fresh from the store. */
function motionFor(frameId: string): MotionTimeline | undefined {
  const frame = useCanvasStore
    .getState()
    .doc.frames.find((f) => f.id === frameId) as { motion?: MotionTimeline } | undefined;
  return frame?.motion;
}

/**
 * Turn a finished record-mode node drag into x/y keyframes at the playhead.
 * Motion x/y are additive translate offsets from layout position, so the new
 * value = sampled value at t + drag delta in world px. Creates the timeline
 * and track on demand; the whole thing is one transaction → one undo step.
 *
 * The store rebuilds `doc` after every dispatch, so we re-read the frame from
 * `getState()` after each mutation rather than holding a stale reference.
 */
export function commitRecordDrag(c: RecordDragCommit): void {
  const store = useCanvasStore.getState();
  if (!store.doc.frames.some((f) => f.id === c.frameId)) return;

  store.beginTransaction('record keyframe');
  try {
    // Ensure a timeline exists.
    if (!motionFor(c.frameId)) {
      store.dispatch({ t: 'motion.init', frameId: c.frameId });
    }

    // Defensive: motion.init NO-OPS on non-animatable frame kinds (patchMotion
    // only animates free/main/tsxFree/tsxMain). If the timeline still doesn't
    // exist the frame can't be keyframed — abort cleanly (the finally closes the
    // empty transaction) instead of crashing on a non-null assertion.
    let tl = motionFor(c.frameId);
    if (!tl) return;

    // Ensure a track targeting this node exists.
    let track = tl.tracks.find((tr) => tr.target === c.cfNodeId);
    if (!track) {
      const trackId = shortId();
      const layerId = shortId();
      // Label is cosmetic; never let a DOM/CSS lookup hiccup abort the record.
      let el: HTMLElement | null = null;
      try {
        el = liveCfElement(c.frameId, c.cfNodeId);
      } catch {
        el = null;
      }
      store.dispatch({
        t: 'motion.addTrack',
        frameId: c.frameId,
        trackId,
        layerId,
        target: c.cfNodeId,
        label: el ? nodeLabel(el) : c.cfNodeId,
      });
      tl = motionFor(c.frameId);
      track = tl?.tracks.find((tr) => tr.id === trackId);
    }
    if (!tl || !track) return;

    const layer = track.layers[0];
    const t = snapTime(c.t, tl.fps);
    const baseX = Number(sampleValueAt(tl, track.id, layer.id, 'x', t)) || 0;
    const baseY = Number(sampleValueAt(tl, track.id, layer.id, 'y', t)) || 0;

    store.dispatch({
      t: 'motion.setKeyframe',
      frameId: c.frameId,
      trackId: track.id,
      layerId: layer.id,
      prop: 'x',
      kf: { t, value: baseX + c.dx / c.zoom, ease: 'easeInOut' },
    });
    store.dispatch({
      t: 'motion.setKeyframe',
      frameId: c.frameId,
      trackId: track.id,
      layerId: layer.id,
      prop: 'y',
      kf: { t, value: baseY + c.dy / c.zoom, ease: 'easeInOut' },
    });
  } finally {
    store.endTransaction();
  }
}
